import { mapAppRide, mapWebRide } from './rides';

const FLUSH_MS = 280;
const HEAL_REFRESH_MS = 5 * 60 * 1000;

export function sortRidesNewestFirst(list) {
  return [...list].sort(
    (a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0)
  );
}

export function upsertMappedRide(list, ride) {
  if (!ride?.id) return list;
  const index = list.findIndex((item) => item.source === ride.source && item.id === ride.id);
  if (index >= 0) {
    const next = list.slice();
    next[index] = ride;
    return sortRidesNewestFirst(next);
  }
  return sortRidesNewestFirst([ride, ...list]);
}

export function removeRideFromList(list, source, id) {
  return list.filter((item) => !(item.source === source && item.id === String(id)));
}

function mapAppRowIfAccepted(row, acceptAppRide) {
  if (!row || !acceptAppRide(row)) return null;
  return mapAppRide(row);
}

/**
 * Subscribe to ride_requests + rides. Applies incremental merges (throttled) and
 * occasional silent full refresh to heal missed events.
 */
export function subscribeAdminRideFeed(supabaseClient, handlers) {
  const {
    onAppChange,
    onWebChange,
    onWebDelete,
    acceptAppRide = () => true,
    onHealRefresh,
  } = handlers;

  if (!supabaseClient) {
    return () => {};
  }

  let flushTimer = null;
  const pendingApp = new Map();
  const pendingWebUpsert = new Map();
  const pendingWebDelete = new Set();

  const flush = () => {
    flushTimer = null;
    pendingApp.forEach((value, id) => {
      if (value === 'DELETE') {
        onAppChange?.({ type: 'delete', id });
        return;
      }
      const mapped = mapAppRowIfAccepted(value, acceptAppRide);
      if (mapped) onAppChange?.({ type: 'upsert', ride: mapped });
    });
    pendingApp.clear();

    pendingWebUpsert.forEach((row) => {
      onWebChange?.({ type: 'upsert', ride: mapWebRide(row) });
    });
    pendingWebUpsert.clear();

    pendingWebDelete.forEach((id) => {
      onWebDelete?.(id);
    });
    pendingWebDelete.clear();
  };

  const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = window.setTimeout(flush, FLUSH_MS);
  };

  const channel = supabaseClient
    .channel('admin-ride-feed')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'ride_requests' },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          pendingApp.set(String(payload.old?.id), 'DELETE');
        } else if (payload.new) {
          pendingApp.set(String(payload.new.id), payload.new);
        }
        scheduleFlush();
      }
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'rides' },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          pendingWebDelete.add(String(payload.old?.id));
        } else if (payload.new) {
          pendingWebUpsert.set(String(payload.new.id), payload.new);
        }
        scheduleFlush();
      }
    )
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        handlers.onSubscribed?.();
      }
    });

  const healTimer = window.setInterval(() => {
    onHealRefresh?.();
  }, HEAL_REFRESH_MS);

  return () => {
    if (flushTimer) window.clearTimeout(flushTimer);
    window.clearInterval(healTimer);
    supabaseClient.removeChannel(channel);
  };
}
